import { FC } from 'react';
import { Navigate, RouteObject, useLocation } from 'react-router-dom';

import { PageChangeTracker } from './app/components/page-change-tracker';
import { ProtectedRoute } from './app/components/protected-route';
import { StepRoute } from './app/components/step-route';
import { PortalType } from './domain/portal';
import { GetStartedPage } from './features/account/pages/get-started-page';
import { GetStartedPageVerify } from './features/account/pages/get-started-page-verify';
import { VerifyPhonePage } from './features/account/pages/verify-phone-page';
import { VerifyPhonePageV2 } from './features/account/pages/verify-phone-page/VerifyPhonePageV2';
import { ApplicationCompletePage } from './features/application/pages/application-complete-page';
import { ApplicationDeniedPage } from './features/application/pages/application-denied-page';
import { ApplicationOfflineReviewPage } from './features/application/pages/application-offline-review-page';
import { BusinessInfoOnePage } from './features/application/pages/business-info-1-page';
import { BusinessInfoTwoPage } from './features/application/pages/business-info-2-page';
import { BusinessInfoVerifyPage } from './features/application/pages/business-info-verify-page';
import { CounterOfferPage } from './features/application/pages/counter-offer-page';
import { CreditLockedPage } from './features/application/pages/credit-locked-page';
import { PersonalInfoPage } from './features/application/pages/personal-info-page';
import { PreCollectUbosPage } from './features/application/pages/pre-collect-ubos-page';
import { PrequalifiedPage } from './features/application/pages/prequalified-page';
import { PreviewCardPage } from './features/application/pages/preview-card-page';
import { SpendIntentPage } from './features/application/pages/spend-intent-page';
import { VerificationOfflineReviewPage } from './features/application/pages/verification-offline-review-page';
import { ReviewApplicationPage } from './features/application-review/pages/review-application-page';
import { DocumentCollectionPage } from './features/document-collection/pages/document-collection-page';
import { PartnerAccountConflictPage } from './features/embedded/pages/partner-account-conflict-page/PartnerAccountConflictPage';
import { FleetSelectionPage } from './features/fleet-verification/pages/fleet-selection-page/FleetSelectionPage';
import { FleetVerifiedPage } from './features/fleet-verification/pages/fleet-verified-page/FleetVerifiedPage';
import { NoMatchingFleet } from './features/fleet-verification/pages/no-matching-fleet/NoMatchingFleet';
import { VerifyUbosPage } from './features/fleet-verification/pages/verify-ubos-page/VerifyUbosPage';
import { PersonaPage } from './features/persona/pages/persona-page';
import { FetchingMxBankDataPage } from './features/revenue-verification/pages/fetching-mx-bank-data-page';
import { VerifyDocumentsUploadPage } from './features/revenue-verification/pages/verify-document-upload-page/verify-documents-upload-page';
import { VerifyRevenueMxPage } from './features/revenue-verification/pages/verify-revenue-mx-page';
import { VerifyRevenuePage } from './features/revenue-verification/pages/verify-revenue-page';
import {
  APPLICATION_COMPLETE,
  APPLICATION_DENIED,
  APPLICATION_UNDER_REVIEW,
  BUSINESS_INFO_ONE,
  BUSINESS_INFO_TWO,
  COUNTER_OFFER,
  CREDIT_LOCKED,
  DOCUMENTS,
  FETCHING_BANK_DATA,
  FLEET_SELECTION,
  FLEET_VERIFIED,
  GET_STARTED,
  NO_MATCHING_FLEET,
  PERSONA,
  PERSONAL_INFO,
  PREQUALIFIED,
  PREVIEW_CARD,
  PRE_COLLECT_UBOS,
  REVIEW_APPLICATION,
  SPEND_INTENT,
  VERIFY_OTP,
  VERIFY_REVENUE,
  VERIFY_REVENUE_DOCS,
  VERIFY_REVENUE_MX,
  PARTNER_ACCOUNT_CONFLICT,
} from './paths';

const RedirectToGetStarted: FC = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: GET_STARTED, search }} replace />;
};

const applyRouteDefinitions: RouteObject[] = [
  { path: GET_STARTED, element: <GetStartedPage /> },
  {
    path: VERIFY_OTP,
    element: (
      <ProtectedRoute conditionKey="verifying">
        <VerifyPhonePage />
      </ProtectedRoute>
    ),
  },
  {
    path: BUSINESS_INFO_ONE,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <BusinessInfoOnePage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: BUSINESS_INFO_TWO,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <BusinessInfoTwoPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: SPEND_INTENT,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <SpendIntentPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: PERSONAL_INFO,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <PersonalInfoPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: PREVIEW_CARD,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <PreviewCardPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: PERSONA,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <PersonaPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: DOCUMENTS,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <DocumentCollectionPage nextAction="collect_documents" portalType="apply" />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: PREQUALIFIED,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <PrequalifiedPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: COUNTER_OFFER,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <CounterOfferPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: REVIEW_APPLICATION,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <ReviewApplicationPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: PRE_COLLECT_UBOS,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <PreCollectUbosPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: CREDIT_LOCKED,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <CreditLockedPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: APPLICATION_UNDER_REVIEW,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute redirect>
          <ApplicationOfflineReviewPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: APPLICATION_DENIED,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <ApplicationDeniedPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: APPLICATION_COMPLETE,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <ApplicationCompletePage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: VERIFY_REVENUE,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <VerifyRevenuePage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: VERIFY_REVENUE_MX,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <VerifyRevenueMxPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: FETCHING_BANK_DATA,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <FetchingMxBankDataPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: VERIFY_REVENUE_DOCS,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <VerifyDocumentsUploadPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  { path: PARTNER_ACCOUNT_CONFLICT, element: <PartnerAccountConflictPage /> },
  {
    path: '*',
    element: <RedirectToGetStarted />,
  },
];

const verifyRouteDefinitions: RouteObject[] = [
  { path: GET_STARTED, element: <GetStartedPageVerify /> },
  { path: VERIFY_OTP, element: <VerifyPhonePageV2 /> },
  {
    path: FLEET_SELECTION,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <FleetSelectionPage />
      </ProtectedRoute>
    ),
  },
  {
    path: NO_MATCHING_FLEET,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <NoMatchingFleet />
      </ProtectedRoute>
    ),
  },
  {
    path: BUSINESS_INFO_ONE,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <BusinessInfoVerifyPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: REVIEW_APPLICATION,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <VerifyUbosPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: DOCUMENTS,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <DocumentCollectionPage nextAction="collect_documents" portalType="verify" />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: APPLICATION_UNDER_REVIEW,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute redirect>
          <VerificationOfflineReviewPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: FLEET_VERIFIED,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <FleetVerifiedPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: APPLICATION_DENIED,
    element: (
      <ProtectedRoute conditionKey="authorized">
        <StepRoute>
          <ApplicationDeniedPage />
        </StepRoute>
      </ProtectedRoute>
    ),
  },
  { path: PARTNER_ACCOUNT_CONFLICT, element: <PartnerAccountConflictPage /> },
  {
    path: '*',
    element: <RedirectToGetStarted />,
  },
];

const createPageTrackingRouteDefinitions = (routes: RouteObject[]) => {
  const allPaths: string[] = routes.map(({ path }) => path).filter((item): item is string => !!item);

  return routes.map((route) => ({
    ...route,
    element: <PageChangeTracker pathsToTrack={allPaths}>{route.element}</PageChangeTracker>,
  }));
};

const routesByPortalType: Record<PortalType, RouteObject[]> = {
  apply: applyRouteDefinitions,
  verify: verifyRouteDefinitions,
};

const getRouteDefinitions = (portalType: PortalType) => {
  return createPageTrackingRouteDefinitions(routesByPortalType[portalType]);
};

export { getRouteDefinitions };
